import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  FiArrowLeft,
  FiBarChart2,
  FiAlertCircle,
  FiRefreshCw,
  FiCheckCircle,
  FiXCircle,
  FiStar,
  FiActivity,
  FiTrendingUp,
  FiBookOpen,
  FiHome,
  FiTarget,
  FiList,
} from 'react-icons/fi';
import { testAPI } from '../../services/api';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import './PracticeTestResults.css';

const PracticeTestResults = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadPracticeTestResult = useCallback(async () => {
    if (!id) {
      setError('No result specified');
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      setError(null);
      const singleResponse = await testAPI.getTestResultById(id);
      const apiResponse = singleResponse.data;
      if (apiResponse.success && apiResponse.data) {
        const practiceResult = apiResponse.data;
        if (practiceResult.testType === 'practice') {
          setResult(practiceResult);
        } else {
          setError('Not a practice test result');
        }
      } else {
        setError(apiResponse.message || 'Practice test result not found');
      }
    } catch (err) {
      if (err.response?.status === 404 || err.response?.status === 403) {
        setError('Practice test result not found.');
      } else {
        setError(err.response?.data?.message || 'Failed to load practice test result. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadPracticeTestResult();
  }, [loadPracticeTestResult]);

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatDuration = (seconds) => {
    if (!seconds) return '0m';
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const remainingSeconds = seconds % 60;
    if (hours > 0) return `${hours}h ${minutes}m ${remainingSeconds}s`;
    if (minutes > 0) return `${minutes}m ${remainingSeconds}s`;
    return `${remainingSeconds}s`;
  };

  const getGrade = (percentage) => {
    if (percentage >= 90) return 'A+';
    if (percentage >= 80) return 'A';
    if (percentage >= 70) return 'B';
    if (percentage >= 60) return 'C';
    if (percentage >= 50) return 'D';
    return 'F';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50/60 flex items-center justify-center">
        <div className="text-center">
          <LoadingSpinner size="large" />
          <p className="text-sm text-slate-500 mt-3">Loading practice test results…</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-50/60">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
          <div className="mb-6">
            <h1 className="text-xl font-semibold text-slate-900 tracking-tight">Practice Test Results</h1>
            <p className="text-sm text-slate-500 mt-0.5">View your practice test score and analysis.</p>
          </div>
          <div className="mb-6 flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
            <FiAlertCircle className="h-4 w-4 shrink-0" />
            <span>{error}</span>
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={loadPracticeTestResult}
              className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-200 rounded-lg hover:bg-slate-50"
            >
              <FiRefreshCw className="h-4 w-4" />
              Try again
            </button>
            <button
              type="button"
              onClick={() => navigate('/test-results')}
              className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-200 rounded-lg hover:bg-slate-50"
            >
              <FiArrowLeft className="h-4 w-4" />
              Back to results
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!result) {
    return (
      <div className="min-h-screen bg-slate-50/60">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
          <div className="mb-6">
            <h1 className="text-xl font-semibold text-slate-900 tracking-tight">Practice Test Results</h1>
            <p className="text-sm text-slate-500 mt-0.5">Practice test result not found.</p>
          </div>
          <button
            type="button"
            onClick={() => navigate('/test-results')}
            className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-200 rounded-lg hover:bg-slate-50"
          >
            <FiArrowLeft className="h-4 w-4" />
            Back to results
          </button>
        </div>
      </div>
    );
  }

  const testTitle = `${result.examType?.toUpperCase()} ${result.subject} Practice Test`;
  const scorePercentage = result.score?.percentage || 0;
  const correctAnswers = result.analytics?.correctAnswers || 0;
  const totalQuestions = result.analytics?.totalQuestions || 0;
  const rawScore = result.score?.raw || 0;
  const performanceLevel =
    scorePercentage >= 80 ? 'Excellent' : scorePercentage >= 60 ? 'Good' : scorePercentage >= 40 ? 'Fair' : 'Needs Improvement';
  const performanceMessage =
    scorePercentage >= 80
      ? 'Strong grasp of the material.'
      : scorePercentage >= 60
        ? 'Continue practicing to improve further.'
        : scorePercentage >= 40
          ? 'Focus on areas that need improvement.'
          : 'Review the topics and try again.';

  return (
    <div className="min-h-screen bg-slate-50/60">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
        <div className="mb-6">
          <button
            type="button"
            onClick={() => navigate('/test-results')}
            className="inline-flex items-center gap-2 text-sm font-medium text-slate-500 hover:text-slate-700 mb-3"
          >
            <FiArrowLeft className="h-4 w-4" />
            Back to results
          </button>
          <h1 className="text-xl font-semibold text-slate-900 tracking-tight">Practice Test Results</h1>
          <p className="text-sm text-slate-500 mt-0.5">{testTitle}</p>
          <div className="flex flex-wrap gap-2 mt-2">
            <span className="rounded-md border border-slate-200 bg-slate-50 px-2 py-0.5 text-xs font-medium text-slate-600">
              {result.subject}
            </span>
            <span className="rounded-md border border-slate-200 bg-slate-50 px-2 py-0.5 text-xs font-medium text-slate-600">
              {result.examType}
            </span>
          </div>
        </div>

        <div className="space-y-4">
          {/* Score & summary card — same pattern as ExamSettings content card */}
          <div className="rounded-lg border border-slate-200 bg-white shadow-sm overflow-hidden">
            <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 border-b border-slate-200 bg-slate-50/80">
              <h2 className="text-sm font-medium text-slate-800">Score</h2>
            </div>
            <div className="p-4">
              <div className="flex flex-wrap items-center gap-6">
                <div className="flex flex-col items-center justify-center w-20 h-20 rounded-full border-2 border-slate-200 bg-white">
                  <span className="text-xl font-semibold text-slate-900">{scorePercentage}%</span>
                  <span className="text-xs font-medium text-slate-500">Grade {getGrade(scorePercentage)}</span>
                </div>
                <div>
                  <p className="text-sm text-slate-700">{correctAnswers} of {totalQuestions} correct</p>
                  <p className="text-sm text-slate-500 mt-0.5">Time spent: {formatDuration(result.session?.timeTaken || 0)}</p>
                  <p className="text-xs text-slate-500 mt-1">Completed {formatDate(result.session?.endTime || result.session?.completedAt || result.createdAt)}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Breakdown + Level — same as ExamSettings two-column / sections */}
          <div className="rounded-lg border border-slate-200 bg-white shadow-sm p-6">
            <h2 className="text-base font-semibold text-slate-900 mb-4">
              Summary <span className="text-slate-500 font-normal">({result.subject}, {result.examType})</span>
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <section>
                <h3 className="flex items-center gap-2 text-sm font-medium text-slate-800 mb-3 pb-2 border-b border-slate-100">
                  <FiList className="h-4 w-4 text-slate-500" />
                  Score breakdown
                </h3>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-slate-500">Correct</span>
                    <span className="font-medium text-slate-900">{correctAnswers}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">Incorrect</span>
                    <span className="font-medium text-slate-900">{totalQuestions - correctAnswers}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">Accuracy</span>
                    <span className="font-medium text-slate-900">{scorePercentage}%</span>
                  </div>
                </div>
              </section>
              <section>
                <h3 className="flex items-center gap-2 text-sm font-medium text-slate-800 mb-3 pb-2 border-b border-slate-100">
                  <FiTarget className="h-4 w-4 text-slate-500" />
                  Level
                </h3>
                <p className="text-sm font-semibold text-slate-900">{performanceLevel}</p>
                <p className="text-sm text-slate-500 mt-1">{performanceMessage}</p>
              </section>
            </div>
          </div>

          {/* Analysis & recommendations — same card pattern, section headers like ExamSettings */}
          <div className="rounded-lg border border-slate-200 bg-white shadow-sm p-6">
            <h2 className="text-base font-semibold text-slate-900 mb-4">Analysis & recommendations</h2>

            {result.aiInsights ? (
              <div className="space-y-6">
                {result.aiInsights.detailedInsight && (
                  <section>
                    <h3 className="flex items-center gap-2 text-sm font-medium text-slate-800 mb-3 pb-2 border-b border-slate-100">
                      <FiBarChart2 className="h-4 w-4 text-slate-500" />
                      Summary
                    </h3>
                    <p className="text-sm text-slate-700 leading-relaxed">{result.aiInsights.detailedInsight}</p>
                  </section>
                )}
                {result.aiInsights.readinessLevel && (
                  <section>
                    <h3 className="flex items-center gap-2 text-sm font-medium text-slate-800 mb-3 pb-2 border-b border-slate-100">
                      <FiTarget className="h-4 w-4 text-slate-500" />
                      Exam readiness
                    </h3>
                    <div className="flex items-center gap-2">
                      {result.aiInsights.readinessLevel === 'exam_ready' && <FiCheckCircle className="h-4 w-4 text-slate-600" />}
                      {result.aiInsights.readinessLevel === 'almost_ready' && <FiStar className="h-4 w-4 text-slate-600" />}
                      {result.aiInsights.readinessLevel === 'needs_improvement' && <FiActivity className="h-4 w-4 text-slate-600" />}
                      {result.aiInsights.readinessLevel === 'not_ready' && <FiXCircle className="h-4 w-4 text-slate-600" />}
                      <span className="text-sm font-semibold text-slate-900">
                        {result.aiInsights.readinessLevel === 'exam_ready' && 'Exam ready'}
                        {result.aiInsights.readinessLevel === 'almost_ready' && 'Almost ready'}
                        {result.aiInsights.readinessLevel === 'needs_improvement' && 'Needs improvement'}
                        {result.aiInsights.readinessLevel === 'not_ready' && 'Not ready'}
                      </span>
                    </div>
                    <p className="text-sm text-slate-500 mt-1">
                      {result.aiInsights.readinessLevel === 'exam_ready' && 'You are ready for the exam.'}
                      {result.aiInsights.readinessLevel === 'almost_ready' && 'Focus on weak areas to get exam ready.'}
                      {result.aiInsights.readinessLevel === 'needs_improvement' && 'More preparation needed before the exam.'}
                      {result.aiInsights.readinessLevel === 'not_ready' && 'Significant preparation is needed before attempting the exam.'}
                    </p>
                  </section>
                )}
                {result.aiInsights.strengths && result.aiInsights.strengths.length > 0 && (
                  <section>
                    <h3 className="flex items-center gap-2 text-sm font-medium text-slate-800 mb-3 pb-2 border-b border-slate-100">
                      <FiCheckCircle className="h-4 w-4 text-slate-500" />
                      Strengths
                    </h3>
                    <ul className="space-y-2">
                      {result.aiInsights.strengths.map((s, i) => (
                        <li key={i} className="text-sm text-slate-700 flex items-start gap-2">
                          <FiCheckCircle className="h-4 w-4 shrink-0 mt-0.5 text-slate-500" />
                          <span>{s.area || s.description}</span>
                        </li>
                      ))}
                    </ul>
                  </section>
                )}
                {result.aiInsights.weaknesses && result.aiInsights.weaknesses.length > 0 && (
                  <section>
                    <h3 className="flex items-center gap-2 text-sm font-medium text-slate-800 mb-3 pb-2 border-b border-slate-100">
                      <FiXCircle className="h-4 w-4 text-slate-500" />
                      Areas for improvement
                    </h3>
                    <ul className="space-y-2">
                      {result.aiInsights.weaknesses.map((w, i) => (
                        <li key={i} className="text-sm text-slate-700 flex items-start gap-2">
                          <FiXCircle className="h-4 w-4 shrink-0 mt-0.5 text-slate-500" />
                          <span>{w.area}: {w.description}</span>
                        </li>
                      ))}
                    </ul>
                  </section>
                )}
                {result.aiInsights.studyPlan && result.aiInsights.studyPlan.length > 0 && (
                  <section>
                    <h3 className="flex items-center gap-2 text-sm font-medium text-slate-800 mb-3 pb-2 border-b border-slate-100">
                      <FiBookOpen className="h-4 w-4 text-slate-500" />
                      Study plan
                    </h3>
                    <ul className="space-y-2">
                      {result.aiInsights.studyPlan.slice(0, 5).map((p, i) => (
                        <li key={i} className="text-sm text-slate-700">
                          <span className="font-medium text-slate-900">{p.topic}</span>
                          {p.priority && <span className="text-slate-500 ml-1">({p.priority})</span>}
                        </li>
                      ))}
                    </ul>
                  </section>
                )}
                {/* Study recommendations — AI list plus PassQik-specific (use platform, join study group + virtual teachers) */}
                {((result.aiInsights.aiRecommendations && result.aiInsights.aiRecommendations.length > 0) ||
                  (result.aiInsights.recommendations && result.aiInsights.recommendations.length > 0)) && (
                  <section>
                    <h3 className="flex items-center gap-2 text-sm font-medium text-slate-800 mb-3 pb-2 border-b border-slate-100">
                      <FiTrendingUp className="h-4 w-4 text-slate-500" />
                      Study recommendations
                    </h3>
                    <ul className="space-y-2">
                      {(result.aiInsights.aiRecommendations || result.aiInsights.recommendations || []).map((rec, i) => (
                        <li key={i} className="text-sm text-slate-700 flex items-start gap-2">
                          <FiTrendingUp className="h-4 w-4 shrink-0 mt-0.5 text-slate-500" />
                          <span>{typeof rec === 'string' ? rec : rec.text || rec.description}</span>
                        </li>
                      ))}
                      <li className="text-sm text-slate-700 flex items-start gap-2">
                        <FiTrendingUp className="h-4 w-4 shrink-0 mt-0.5 text-slate-500" />
                        <span>Use PassQik for more practice tests so you can strengthen your weak areas.</span>
                      </li>
                      <li className="text-sm text-slate-700 flex items-start gap-2">
                        <FiTrendingUp className="h-4 w-4 shrink-0 mt-0.5 text-slate-500" />
                        <span>Join a study group on PassQik and subscribe to our virtual teachers to get help in the areas you&apos;re lacking.</span>
                      </li>
                    </ul>
                  </section>
                )}
                {result.aiInsights.predictedExamScore && (result.aiInsights.predictedExamScore.waec != null || result.aiInsights.predictedExamScore.jamb != null || result.aiInsights.predictedExamScore.postUme != null) && (
                  <section>
                    <h3 className="flex items-center gap-2 text-sm font-medium text-slate-800 mb-3 pb-2 border-b border-slate-100">
                      <FiBarChart2 className="h-4 w-4 text-slate-500" />
                      Predicted performance
                    </h3>
                    <div className="flex flex-wrap gap-3">
                      {result.aiInsights.predictedExamScore.waec != null && (
                        <div className="text-center min-w-[4rem] py-2 px-3 rounded-lg border border-slate-200 bg-slate-50/50">
                          <span className="block text-xs text-slate-500">WAEC</span>
                          <span className="text-lg font-semibold text-slate-900">{result.aiInsights.predictedExamScore.waec}%</span>
                        </div>
                      )}
                      {result.aiInsights.predictedExamScore.jamb != null && (
                        <div className="text-center min-w-[4rem] py-2 px-3 rounded-lg border border-slate-200 bg-slate-50/50">
                          <span className="block text-xs text-slate-500">JAMB</span>
                          <span className="text-lg font-semibold text-slate-900">{result.aiInsights.predictedExamScore.jamb}%</span>
                        </div>
                      )}
                      {result.aiInsights.predictedExamScore.postUme != null && (
                        <div className="text-center min-w-[4rem] py-2 px-3 rounded-lg border border-slate-200 bg-slate-50/50">
                          <span className="block text-xs text-slate-500">Post-UME</span>
                          <span className="text-lg font-semibold text-slate-900">{result.aiInsights.predictedExamScore.postUme}%</span>
                        </div>
                      )}
                    </div>
                  </section>
                )}
                <p className="text-xs text-slate-500 pt-2 border-t border-slate-100">
                  Insights are based on your test performance. Use alongside your teacher&apos;s feedback.
                </p>
              </div>
            ) : (
              <p className="text-sm text-slate-500">
                Insight is available when a syllabus is set for this subject and exam type. Review your score and use Review Answers for explanations.
              </p>
            )}
          </div>

          {/* Actions — same button style as ExamSettings / PastQuestions */}
          <div className="pt-4 flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => navigate('/practice-tests')}
              className="inline-flex items-center gap-2 px-4 py-2.5 bg-slate-900 text-white text-sm font-medium rounded-lg hover:bg-slate-800 transition-colors shadow-sm"
            >
              <FiBookOpen className="h-4 w-4" />
              Take another test
            </button>
            <button
              type="button"
              onClick={() => navigate('/test-results')}
              className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-200 rounded-lg hover:bg-slate-50"
            >
              <FiBarChart2 className="h-4 w-4" />
              All results
            </button>
            <button
              type="button"
              onClick={() => navigate('/dashboard')}
              className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-200 rounded-lg hover:bg-slate-50"
            >
              <FiHome className="h-4 w-4" />
              Dashboard
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PracticeTestResults;
